import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Minus, Truck, Check, ShieldCheck, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCentsAsUsd } from "@/lib/money";
import {
  contrastTextColorHex,
  mixHexColors,
  mutedOnSurfaceHex,
  getContrastTextColor,
} from "@/utils/colorUtils";
import { CheckoutMarkdown } from "./CheckoutMarkdown";
import {
  isSoldOut,
  resolveLineItemStock,
} from "@/lib/validateCheckoutInventory";
import { CheckoutTipSelector } from "./CheckoutTipSelector";

export type CheckoutAppearanceColors = {
  backgroundColor?: string;
  accentColor?: string;
};

/** Shared copy/styling for shipping protection upsell (checkout card + receipt line item). */
function ShippingProtectionBenefits({
  className,
  checkoutTheme,
  tone,
}: {
  className?: string;
  checkoutTheme: {
    calloutPrice: string;
    mutedText: string;
    shippingProtectionPriceColor?: string;
  } | null;
  /** Callout card uses shipping protection line color when set, else tinted callout price. */
  tone: "callout" | "lineItem";
}) {
  const colorStyle =
    checkoutTheme != null
      ? {
          color:
            tone === "callout"
              ? checkoutTheme.shippingProtectionPriceColor ?? checkoutTheme.calloutPrice
              : checkoutTheme.mutedText,
        }
      : undefined;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div
        className={cn(
          "flex items-center gap-2 text-sm",
          !checkoutTheme && "text-gray-600 dark:text-gray-400"
        )}
        style={colorStyle}
      >
        <Check size={12} color="#8b8b8b" className="shrink-0" aria-hidden />
        Damage or Defects
      </div>
      <div
        className={cn(
          "flex items-center gap-2 text-sm",
          !checkoutTheme && "text-gray-600 dark:text-gray-400"
        )}
        style={colorStyle}
      >
        <Check size={12} color="#8b8b8b" className="shrink-0" aria-hidden />
        Fast replacement
      </div>
    </div>
  );
}

interface ProductVariantOption {
  _id: string;
  name: string;
  /** When 0, option is shown but not selectable (out of stock). */
  quantity?: number;
}

interface Product {
  _id: string;
  name?: string;
  variantName?: string;
  /** Integer cents */
  price: number;
  /** Markdown; rendered on checkout */
  description?: string;
  images?: { url: string; thumbnailUrl?: string }[];
  quantityRules?: { minimum: number; maximum: number };
  inventoryQuantity?: number;
  variants?: ProductVariantOption[];
  /** Effective variant for this cart line when `variants` is non-empty */
  selectedVariantId?: string;
  /** True while product has variants but none chosen yet (subtotal excludes this line). */
  pricePendingVariant?: boolean;
  /** Highlight variant select after failed Place Order validation */
  variantSelectNeedsAttention?: boolean;
  /** When true, hide the Remove control for this line (see Product.disableRemove). */
  disableRemove?: boolean;
}

interface OrderQuantitySelectorProps {
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  /** Product subtotal / pay-what-you-can charge in integer cents (excludes shipping) */
  total?: number;
  /** Shipping in integer cents */
  shippingCents?: number;
  /** Label for the shipping line (e.g. zone + option) */
  shippingLabel?: string;
  /** Added to total when shipping protection checkbox is checked (integer cents) */
  shippingProtectionCents?: number;
  shippingProtectionSelectable?: boolean;
  shippingProtectionUnitCents?: number;
  shippingProtectionChecked?: boolean;
  onShippingProtectionChange?: (checked: boolean) => void;
  /** Added to total when order bump checkbox is checked (integer cents) */
  orderBumpCents?: number;
  orderBumpSelectable?: boolean;
  orderBumpLineTotalCents?: number;
  orderBumpTitle?: string;
  orderBumpDescription?: string;
  orderBumpChecked?: boolean;
  onOrderBumpChange?: (checked: boolean) => void;
  orderBumps?: Array<{
    title: string;
    description?: string;
    lineTotalCents: number;
    productId?: string;
    variants?: Array<{ _id: string; name: string; price?: number }>;
    selectedVariantIds?: string[];
    pricePendingVariant?: boolean;
    variantSelectNeedsAttention?: boolean;
    quantity?: number;
    maxQuantity?: number;
  }>;
  orderBumpsOptIn?: boolean[];
  onOrderBumpChangePlural?: (index: number, checked: boolean) => void;
  onOrderBumpQuantityChange?: (index: number, quantity: number) => void;
  onOrderBumpVariantChange?: (index: number, slotIndex: number, variantId: string) => void;
  /** Tip in integer cents */
  tipAmount?: number;
  /** Hide steppers and show quantity as text (e.g. order confirmation). */
  readOnly?: boolean;
  /** When set, borders/backgrounds and secondary text follow checkout branding with sufficient contrast. */
  checkoutColors?: CheckoutAppearanceColors | null;
  /** When false, the Shipping row is hidden and shipping is omitted from the footer total (e.g. before the payment step). */
  showShippingLineItem?: boolean;
  /** When false, Shipping Protection is hidden and its cents are omitted from the footer total (e.g. before the payment step). */
  showShippingProtectionLineItem?: boolean;
  /** When false, order bump is hidden and its cents are omitted from the footer total. */
  showOrderBumpLineItem?: boolean;
  /** When false, the Tip row is hidden and tip cents are omitted from the footer total. */
  showTipLineItem?: boolean;
  /** When true, the divider and footer total are omitted (rendered elsewhere, e.g. below the tip selector). */
  hideFooterTotal?: boolean;
  /** When true, the shipping protection callout uses a solid accent background (payment step branding). */
  shippingProtectionAccentBackground?: boolean;
  /** Called with the row index in `products`; the first row (index 0) cannot be removed. */
  onRemoveLine?: ({ displayIndex }: { displayIndex: number }) => void;
  onLineVariantChange?: ({
    displayIndex,
    variantId,
  }: {
    displayIndex: number;
    variantId: string;
  }) => void;
  tipping?: {
    enabled: boolean;
    defaultSelected?: boolean;
    amount1?: number;
    amount2?: number;
    amount3?: number;
  };
  tipTitle?: string;
  tipDescription?: string;
  onTipCentsChange?: ({ tipAmountCents }: { tipAmountCents: number }) => void;
  onTipSelectionChange?: ({ optedIn, hasSelected }: { optedIn: boolean; hasSelected: boolean }) => void;
  showTipSelector?: boolean;
  idPrefix?: string;
  highlightIncomplete?: boolean;
}

const VARIANT_SELECT_CLASS =
  "border-input mt-1.5 h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:bg-input/30 md:text-sm";

const VARIANT_SELECT_INVALID_RING =
  "border-red-500 dark:border-red-500 aria-invalid:border-red-500 ring-2 ring-red-500/25";

function useCheckoutFooterTheme(checkoutColors: CheckoutAppearanceColors | null) {
  return useMemo(() => {
    if (checkoutColors == null) return null;
    const surface = checkoutColors.backgroundColor?.trim() || "#ffffff";
    const primaryText = contrastTextColorHex(surface);
    const dividerColor = mixHexColors(
      primaryText === "#1a1a1a" ? "#000000" : "#ffffff",
      surface,
      0.2
    );
    return { primaryText, dividerColor };
  }, [checkoutColors]);
}

export function CheckoutOrderFooterTotal({
  amountCents,
  checkoutColors = null,
}: {
  amountCents: number;
  checkoutColors?: CheckoutAppearanceColors | null;
}) {
  const checkoutTheme = useCheckoutFooterTheme(checkoutColors);

  return (
    <>
      <hr
        className={cn(
          "my-4 border-0 border-t border-solid",
          !checkoutTheme && "border-gray-300 dark:border-gray-600"
        )}
        style={checkoutTheme ? { borderTopColor: checkoutTheme.dividerColor } : undefined}
      />
      <div
        className="flex justify-end font-semibold text-lg tabular-nums"
        style={checkoutTheme ? { color: checkoutTheme.primaryText } : undefined}
      >
        Total: ${formatCentsAsUsd(amountCents)}
      </div>
    </>
  );
}

export function OrderQuantitySelector({
  products,
  quantities,
  onQuantityChange,
  total,
  shippingCents = 0,
  shippingLabel,
  shippingProtectionCents = 0,
  shippingProtectionSelectable = false,
  shippingProtectionUnitCents = 0,
  shippingProtectionChecked = false,
  onShippingProtectionChange,
  orderBumpCents = 0,
  orderBumpSelectable = false,
  orderBumpLineTotalCents = 0,
  orderBumpTitle = "",
  orderBumpDescription,
  orderBumpChecked = false,
  onOrderBumpChange,
  tipAmount = 0,
  readOnly = false,
  checkoutColors = null,
  showShippingLineItem = true,
  showShippingProtectionLineItem = true,
  showOrderBumpLineItem = true,
  showTipLineItem = false,
  hideFooterTotal = false,
  shippingProtectionAccentBackground = false,
  onRemoveLine,
  onLineVariantChange,
  orderBumps,
  orderBumpsOptIn,
  onOrderBumpChangePlural,
  onOrderBumpQuantityChange,
  onOrderBumpVariantChange,
  tipping,
  tipTitle,
  tipDescription,
  onTipCentsChange,
  onTipSelectionChange,
  showTipSelector,
  idPrefix = "checkout",
  highlightIncomplete = false,
}: OrderQuantitySelectorProps) {
  const resolvedBumpsForRender = useMemo(() => {
    if (orderBumps && orderBumpsOptIn) {
      return orderBumps.map((b, i) => ({
        title: b.title,
        description: b.description,
        lineTotalCents: b.lineTotalCents,
        checked: orderBumpsOptIn[i] === true,
        onChange: (c: boolean) => onOrderBumpChangePlural?.(i, c),
        id: `checkout-order-bump-${i}`,
        productId: b.productId,
        variants: b.variants,
        selectedVariantIds: b.selectedVariantIds,
        pricePendingVariant: b.pricePendingVariant,
        variantSelectNeedsAttention: b.variantSelectNeedsAttention,
        quantity: b.quantity,
        maxQuantity: b.maxQuantity,
        index: i,
      }));
    }
    if (orderBumpSelectable && orderBumpLineTotalCents > 0 && orderBumpTitle && orderBumpTitle.trim() !== "") {
      return [{
        title: orderBumpTitle,
        description: orderBumpDescription,
        lineTotalCents: orderBumpLineTotalCents,
        checked: orderBumpChecked === true,
        onChange: (c: boolean) => onOrderBumpChange?.(c),
        id: "checkout-order-bump",
        index: 0,
      }];
    }
    return [];
  }, [orderBumps, orderBumpsOptIn, onOrderBumpChangePlural, orderBumpSelectable, orderBumpLineTotalCents, orderBumpTitle, orderBumpDescription, orderBumpChecked, onOrderBumpChange]);
  const checkoutTheme = useMemo(() => {
    if (checkoutColors == null) return null;
    const surface = checkoutColors.backgroundColor?.trim() || "#ffffff";
    const accent = checkoutColors.accentColor?.trim() || "#6b7280";
    const primaryText = contrastTextColorHex(surface);
    const mutedText = mutedOnSurfaceHex(surface);
    const calloutBg = mixHexColors(accent, surface, 0.14);
    const calloutBorder = mixHexColors(accent, surface, 0.45);
    const calloutText = contrastTextColorHex(calloutBg);
    const calloutPrice = mutedOnSurfaceHex(calloutBg);
    const accentContrast = contrastTextColorHex(accent);
    const truckBg = mixHexColors(accent, surface, 0.1);
    const dividerColor = mixHexColors(
      primaryText === "#1a1a1a" ? "#000000" : "#ffffff",
      surface,
      0.2
    );
    const focusRingClass =
      getContrastTextColor(surface) === "white"
        ? "focus-within:ring-white/45"
        : "focus-within:ring-black/30";
    const checkboxBorder = mixHexColors(accent, calloutBg, 0.55);
    const solidAccentSp = shippingProtectionAccentBackground;
    const shippingProtectionBg = solidAccentSp ? accent : calloutBg;
    const shippingProtectionBorder = solidAccentSp
      ? mixHexColors(accent, surface, 0.5)
      : calloutBorder;
    const shippingProtectionTitleColor = solidAccentSp ? accentContrast : calloutText;
    const shippingProtectionPriceColor = solidAccentSp ? mutedOnSurfaceHex(accent) : calloutPrice;
    const shippingProtectionCheckboxBorder = solidAccentSp
      ? mixHexColors(accent, accentContrast, 0.45)
      : checkboxBorder;
    const shippingProtectionCheckboxCheckedBg = solidAccentSp ? accentContrast : accent;
    const shippingProtectionCheckboxCheckedFg = solidAccentSp ? accent : accentContrast;
    const orderBumpBorder = accent;
    const orderBumpCheckboxBorder = mixHexColors(accent, surface, 0.55);
    return {
      surface,
      accent,
      primaryText,
      mutedText,
      calloutBg,
      calloutBorder,
      calloutText,
      calloutPrice,
      accentContrast,
      truckBg,
      dividerColor,
      focusRingClass,
      checkboxBorder,
      shippingProtectionBg,
      shippingProtectionBorder,
      shippingProtectionTitleColor,
      shippingProtectionPriceColor,
      shippingProtectionCheckboxBorder,
      shippingProtectionCheckboxCheckedBg,
      shippingProtectionCheckboxCheckedFg,
      orderBumpBorder,
      orderBumpTitleColor: primaryText,
      orderBumpPriceColor: mutedText,
      orderBumpCheckboxBorder,
      orderBumpCheckboxCheckedBg: accent,
      orderBumpCheckboxCheckedFg: accentContrast,
    };
  }, [checkoutColors, shippingProtectionAccentBackground]);

  return (
    <div className="space-y-2 my-4">
      {products.map((product, displayIndex) => {
        // TODO: remove this name-based hack; model shipping protection line items explicitly (e.g. kind flag) instead of matching title string.
        const isShippingProtectionLineItem =
          (product.name ?? "").trim() === "Shipping Protection";
        const firstImage = product.images?.[0];
        const imageUrl =
          !isShippingProtectionLineItem
            ? firstImage?.thumbnailUrl ?? firstImage?.url
            : undefined;
        const min = product.quantityRules?.minimum ?? 1;
        const max = product.quantityRules?.maximum ?? 10;
        const showQuantitySelect = max > min;
        const currentQty = quantities[product._id] ?? min;
        const variants = product.variants;
        const singleVariant = variants?.length === 1 ? variants[0] : undefined;
        const singleVariantStock =
          singleVariant != null
            ? resolveLineItemStock({
                product: { inventoryQuantity: product.inventoryQuantity, variants },
                variantId: String(singleVariant._id),
              })
            : undefined;
        const noVariantStock =
          !variants?.length
            ? resolveLineItemStock({
                product: { inventoryQuantity: product.inventoryQuantity, variants },
              })
            : undefined;
        const variantTextLabel =
          singleVariant != null
            ? isSoldOut({ stock: singleVariantStock })
              ? `${singleVariant.name} (Sold out)`
              : singleVariant.name
            : !variants?.length && isSoldOut({ stock: noVariantStock })
              ? "Sold out"
              : readOnly && product.selectedVariantId && variants?.length
                ? variants.find(
                    (v) => String(v._id) === String(product.selectedVariantId)
                  )?.name ?? ""
                : null;
        const showVariantDropdown =
          !readOnly &&
          (variants?.length ?? 0) > 1 &&
          onLineVariantChange != null;
        const needsVariantChoice = showVariantDropdown;
        const variantChosen =
          !needsVariantChoice ||
          (!product.pricePendingVariant && Boolean(product.selectedVariantId?.trim()));
        const showQuantityControls =
          !readOnly && showQuantitySelect && variantChosen;

        return (
          <div key={product._id}>
            {displayIndex > 0 && (
              <hr
                className={cn(
                  "mb-4 border-0 border-t border-solid",
                  !checkoutTheme && "border-gray-200 dark:border-gray-700"
                )}
                style={checkoutTheme ? { borderTopColor: checkoutTheme.dividerColor } : undefined}
              />
            )}
          <div
            className="flex items-start gap-4"
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={[product.name, product.variantName].filter(Boolean).join(" — ") || "Product"}
                className="h-14 w-14 object-cover rounded-md shrink-0"
              />
            ) : isShippingProtectionLineItem ? (
              <div
                className={cn(
                  "flex h-14 w-14 shrink-0 items-center justify-center rounded-md",
                  !checkoutTheme && "bg-gray-100 dark:bg-gray-800"
                )}
                style={checkoutTheme ? { backgroundColor: checkoutTheme.truckBg } : undefined}
                aria-hidden
              >
                <ShieldCheck
                  className={cn("h-6 w-6", !checkoutTheme && "text-gray-600 dark:text-gray-400")}
                  style={checkoutTheme ? { color: checkoutTheme.mutedText } : undefined}
                  strokeWidth={1.75}
                />
              </div>
            ) : (
              <div className="h-14 w-14 shrink-0" aria-hidden />
            )}
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div
                  className="min-w-0 flex-1 font-medium"
                  style={checkoutTheme ? { color: checkoutTheme.primaryText } : undefined}
                >
                  {product.name || `Product ${product._id}`}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <div
                    className={cn(
                      "text-right text-sm tabular-nums",
                      !checkoutTheme && "text-gray-500 dark:text-gray-400"
                    )}
                    style={checkoutTheme ? { color: checkoutTheme.mutedText } : undefined}
                  >
                    {product.pricePendingVariant ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <>${formatCentsAsUsd(product.price ?? 0)}</>
                    )}
                  </div>
                  {readOnly && currentQty > 1 ? (
                    <div
                      className="text-sm font-medium tabular-nums"
                      style={checkoutTheme ? { color: checkoutTheme.primaryText } : undefined}
                    >
                      ×{currentQty}
                    </div>
                  ) : null}
                </div>
              </div>
              {product.description?.trim() ? (
                <CheckoutMarkdown markdown={product.description.trim()} />
              ) : null}
              {showVariantDropdown && variants ? (
                <div className="w-full">
                  <select
                    id={`checkout-product-variant-${displayIndex}`}
                    aria-label="Variant"
                    aria-invalid={product.variantSelectNeedsAttention ? true : undefined}
                    className={cn(
                      VARIANT_SELECT_CLASS,
                      product.variantSelectNeedsAttention && VARIANT_SELECT_INVALID_RING
                    )}
                    value={product.selectedVariantId ?? ""}
                    onChange={(e) =>
                      onLineVariantChange!({
                        displayIndex,
                        variantId: e.target.value,
                      })
                    }
                  >
                    <option value="">Choose option...</option>
                    {variants.map((v) => {
                      const stock = resolveLineItemStock({
                        product: {
                          inventoryQuantity: product.inventoryQuantity,
                          variants,
                        },
                        variantId: String(v._id),
                      });
                      const soldOut = isSoldOut({ stock });
                      return (
                      <option
                        key={v._id}
                        value={String(v._id)}
                        disabled={soldOut}
                      >
                        {soldOut ? `${v.name} (Sold out)` : v.name}
                      </option>
                      );
                    })}
                  </select>
                </div>
              ) : null}
              <div className="min-w-0">
                {variantTextLabel ? (
                  <div
                    className={cn(
                      "mt-1 text-sm font-normal",
                      !checkoutTheme && "text-gray-600 dark:text-gray-400"
                    )}
                    style={checkoutTheme ? { color: checkoutTheme.mutedText } : undefined}
                  >
                    {variantTextLabel}
                  </div>
                ) : null}
                {showQuantityControls ? (
                  <div className="mt-1.5 flex items-center">
                    <div className="flex items-center gap-2 border rounded-md">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (currentQty > min) {
                            onQuantityChange(product._id, currentQty - 1);
                          }
                        }}
                        disabled={currentQty <= min}
                        className="h-8 w-8"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="min-w-[2rem] text-center font-medium">
                        {currentQty}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (currentQty < max) {
                            onQuantityChange(product._id, currentQty + 1);
                          }
                        }}
                        disabled={currentQty >= max}
                        className="h-8 w-8"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}
                {product.variantName?.trim() &&
                (readOnly
                  ? !variantTextLabel
                  : !(variants?.length && onLineVariantChange)) ? (
                  <div
                    className={cn(
                      readOnly && "mt-1",
                      "text-sm font-normal",
                      !checkoutTheme && "text-gray-600 dark:text-gray-400"
                    )}
                    style={checkoutTheme ? { color: checkoutTheme.mutedText } : undefined}
                  >
                    {product.variantName.trim()}
                  </div>
                ) : null}
                {isShippingProtectionLineItem && readOnly ? (
                  <ShippingProtectionBenefits
                    className="mt-1.5"
                    checkoutTheme={checkoutTheme}
                    tone="lineItem"
                  />
                ) : null}
                {!readOnly &&
                displayIndex > 0 &&
                onRemoveLine != null &&
                !product.disableRemove ? (
                  <div className="mt-1.5 flex justify-end">
                    <button
                      type="button"
                      className="flex items-center gap-1 rounded text-xs underline-offset-2 hover:underline text-red-600 dark:text-red-400"
                      onClick={() => onRemoveLine({ displayIndex })}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      Remove
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          </div>
        );
      })}
      {shippingCents > 0 && showShippingLineItem ? (
        <div className="flex items-center gap-4">
          <div
            className={cn(
              "flex h-14 w-14 shrink-0 items-center justify-center rounded-md",
              !checkoutTheme && "bg-gray-100 dark:bg-gray-800"
            )}
            style={checkoutTheme ? { backgroundColor: checkoutTheme.truckBg } : undefined}
            aria-hidden
          >
            <Truck
              className={cn("h-6 w-6", !checkoutTheme && "text-gray-600 dark:text-gray-400")}
              style={checkoutTheme ? { color: checkoutTheme.mutedText } : undefined}
              strokeWidth={1.75}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <div
                className="min-w-0 font-medium"
                style={checkoutTheme ? { color: checkoutTheme.primaryText } : undefined}
              >
                Shipping
              </div>
              <div
                className={cn(
                  "shrink-0 text-right text-sm tabular-nums",
                  !checkoutTheme && "text-gray-500 dark:text-gray-400"
                )}
                style={checkoutTheme ? { color: checkoutTheme.mutedText } : undefined}
              >
                ${formatCentsAsUsd(shippingCents)}
              </div>
            </div>
            {shippingLabel ? (
              <div
                className={cn(
                  "text-sm font-normal",
                  !checkoutTheme && "text-gray-600 dark:text-gray-400"
                )}
                style={checkoutTheme ? { color: checkoutTheme.mutedText } : undefined}
              >
                {shippingLabel}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {showShippingProtectionLineItem &&
      shippingProtectionSelectable &&
      shippingProtectionUnitCents > 0 &&
      onShippingProtectionChange != null ? (
          <label
            htmlFor="checkout-shipping-protection"
            className={cn(
              "flex w-full min-w-0 cursor-pointer flex-col gap-2 rounded-lg border-2 px-4 py-3.5 shadow-sm transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2",
              checkoutTheme
                ? cn(
                    "focus-within:ring-2 focus-within:ring-offset-2",
                    checkoutTheme.focusRingClass
                  )
                : "border-primary/35 bg-primary/8 ring-offset-background hover:border-primary/55 hover:bg-primary/12 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 dark:border-primary/40 dark:bg-primary/12 dark:hover:border-primary/60 dark:hover:bg-primary/18"
            )}
            style={
              checkoutTheme
                ? {
                    backgroundColor: checkoutTheme.shippingProtectionBg,
                    borderColor: checkoutTheme.shippingProtectionBorder,
                    color: checkoutTheme.shippingProtectionTitleColor,
                    ["--checkout-accent" as string]:
                      checkoutTheme.shippingProtectionCheckboxCheckedBg,
                    ["--checkout-accent-contrast" as string]:
                      checkoutTheme.shippingProtectionCheckboxCheckedFg,
                    ["--checkout-checkbox-border" as string]:
                      checkoutTheme.shippingProtectionCheckboxBorder,
                    ["--tw-ring-offset-color" as string]: checkoutTheme.surface,
                  }
                : undefined
            }
          >
            <div className="flex w-full min-w-0 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <Checkbox
                  id="checkout-shipping-protection"
                  checked={shippingProtectionChecked}
                  onCheckedChange={(c) => onShippingProtectionChange(c === true)}
                  className={cn(
                    "size-5 shrink-0 rounded-[5px]",
                    checkoutTheme &&
                      "border-[color:var(--checkout-checkbox-border)] data-[state=checked]:border-[var(--checkout-accent)] data-[state=checked]:bg-[var(--checkout-accent)] data-[state=checked]:text-[var(--checkout-accent-contrast)]"
                  )}
                />
                <span className="text-base font-semibold leading-snug">
                  Shipping Protection
                </span>
              </div>
              <span
                className={cn(
                  "shrink-0 text-right text-sm font-semibold tabular-nums",
                  !checkoutTheme && "text-foreground/80"
                )}
                style={
                  checkoutTheme
                    ? { color: checkoutTheme.shippingProtectionPriceColor }
                    : undefined
                }
              >
                ${formatCentsAsUsd(shippingProtectionUnitCents)}
              </span>
            </div>
            {shippingProtectionChecked ? (
              <ShippingProtectionBenefits
                className="pl-8"
                checkoutTheme={checkoutTheme}
                tone="callout"
              />
            ) : null}
          </label>
      ) : null}
      {showOrderBumpLineItem && resolvedBumpsForRender.map((bump) => {
        const bumpNeedsAttention = highlightIncomplete && bump.checked && bump.pricePendingVariant;
        return (
          <div
            key={bump.id}
            data-invalid={bump.checked && bump.pricePendingVariant ? "true" : undefined}
            className={cn(
              "flex w-full min-w-0 flex-col gap-2 rounded-lg border-2 px-4 py-3.5 shadow-sm transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2",
              bumpNeedsAttention && "ring-2 ring-red-500/25 border-red-500",
              !bumpNeedsAttention && (checkoutTheme
                ? cn(
                    "focus-within:ring-2 focus-within:ring-offset-2",
                    checkoutTheme.focusRingClass
                  )
                : "border-primary bg-transparent ring-offset-background hover:border-primary/80 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2")
            )}
            style={
              checkoutTheme
                ? {
                    backgroundColor: checkoutTheme.surface,
                    borderColor: bumpNeedsAttention ? "#ef4444" : checkoutTheme.orderBumpBorder,
                    color: checkoutTheme.orderBumpTitleColor,
                    ["--checkout-accent" as string]:
                      checkoutTheme.orderBumpCheckboxCheckedBg,
                    ["--checkout-accent-contrast" as string]:
                      checkoutTheme.orderBumpCheckboxCheckedFg,
                    ["--checkout-checkbox-border" as string]:
                      checkoutTheme.orderBumpCheckboxBorder,
                    ["--tw-ring-offset-color" as string]: checkoutTheme.surface,
                  }
                : bumpNeedsAttention
                  ? { borderColor: "#ef4444" }
                  : undefined
            }
          >
            <div className="flex w-full min-w-0 items-center justify-between gap-3">
              <label htmlFor={bump.id} className="flex min-w-0 items-center gap-3 cursor-pointer select-none">
                <Checkbox
                  id={bump.id}
                  checked={bump.checked}
                  onCheckedChange={(c) => bump.onChange(c === true)}
                  className={cn(
                    "size-5 shrink-0 rounded-[5px]",
                    checkoutTheme &&
                      "border-[color:var(--checkout-checkbox-border)] data-[state=checked]:border-[var(--checkout-accent)] data-[state=checked]:bg-[var(--checkout-accent)] data-[state=checked]:text-[var(--checkout-accent-contrast)]"
                  )}
                />
                <span className="text-base font-semibold leading-snug">
                  {bump.title}
                </span>
              </label>
              <span
                className={cn(
                  "shrink-0 text-right text-sm font-semibold tabular-nums",
                  !checkoutTheme && "text-foreground/80"
                )}
                style={
                  checkoutTheme
                    ? { color: checkoutTheme.orderBumpPriceColor }
                    : undefined
                }
              >
                {bump.pricePendingVariant ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <>${formatCentsAsUsd(bump.lineTotalCents)}</>
                )}
              </span>
            </div>
            {bump.checked && bump.description?.trim() ? (
              <div className="pl-8">
                <CheckoutMarkdown markdown={bump.description} />
              </div>
            ) : null}
            {bump.checked && !readOnly && bump.maxQuantity !== 1 && (
              <div className="pl-8 mt-1 flex items-center" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 border rounded-md bg-transparent">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const currentQty = bump.quantity ?? 1;
                      if (currentQty > 1) {
                        onOrderBumpQuantityChange?.(bump.index, currentQty - 1);
                      }
                    }}
                    disabled={(bump.quantity ?? 1) <= 1}
                    className="h-8 w-8"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="min-w-[2rem] text-center font-medium">
                    {bump.quantity ?? 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const currentQty = bump.quantity ?? 1;
                      const maxQty = bump.maxQuantity ?? 10;
                      if (currentQty < maxQty) {
                        onOrderBumpQuantityChange?.(bump.index, currentQty + 1);
                      }
                    }}
                    disabled={(bump.quantity ?? 1) >= (bump.maxQuantity ?? 10)}
                    className="h-8 w-8"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            {bump.checked && !readOnly && bump.variants && bump.variants.length > 0 && (
              <div className="pl-8 w-full flex flex-col gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                {Array.from({ length: bump.quantity ?? 1 }).map((_, slotIdx) => {
                  const currentVariantId = bump.selectedVariantIds?.[slotIdx] ?? "";
                  return (
                    <div key={slotIdx} className="w-full">
                      {bump.quantity && bump.quantity > 1 && (
                        <label className="text-xs font-semibold mb-1 block opacity-70">
                          Option for item #{slotIdx + 1}
                        </label>
                      )}
                      <select
                        id={`checkout-order-bump-variant-${bump.index}-${slotIdx}`}
                        aria-label={`Variant ${slotIdx + 1}`}
                        aria-invalid={bump.variantSelectNeedsAttention && !currentVariantId ? true : undefined}
                        className={cn(
                          VARIANT_SELECT_CLASS,
                          bump.variantSelectNeedsAttention && !currentVariantId && VARIANT_SELECT_INVALID_RING
                        )}
                        value={currentVariantId}
                        onChange={(e) =>
                          onOrderBumpVariantChange?.(bump.index, slotIdx, e.target.value)
                        }
                      >
                        <option value="">Choose option...</option>
                        {bump.variants!.map((v) => {
                          const soldOut = v.quantity === 0;
                          return (
                            <option key={v._id} value={v._id} disabled={soldOut}>
                              {soldOut ? `${v.name} (Sold out)` : v.name}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
            {bump.checked && readOnly && bump.variants && bump.variants.length > 0 && (
              <div className="pl-8 mt-1.5 flex flex-col gap-1 text-sm opacity-80">
                {bump.selectedVariantIds?.map((vid, slotIdx) => {
                  const variant = bump.variants!.find((v) => v._id === vid);
                  return variant ? (
                    <div key={slotIdx}>
                      Item #{slotIdx + 1}: {variant.name}
                    </div>
                  ) : null;
                })}
              </div>
            )}
            {bump.checked && readOnly && bump.quantity && bump.quantity > 1 && (!bump.variants || bump.variants.length === 0) && (
              <div className="pl-8 mt-1 text-sm opacity-80">
                Quantity: {bump.quantity}
              </div>
            )}
          </div>
        );
      })}
      {showTipSelector && tipping?.enabled ? (
        <CheckoutTipSelector
          tipping={tipping}
          title={tipTitle}
          description={tipDescription}
          colors={checkoutColors ?? undefined}
          onTipCentsChange={onTipCentsChange}
          onTipSelectionChange={onTipSelectionChange}
          idPrefix={idPrefix}
          highlightIncomplete={highlightIncomplete}
        />
      ) : null}
      {showTipLineItem && tipAmount > 0 ? (
        <div className="flex items-baseline justify-between gap-3">
          <div
            className="font-medium"
            style={checkoutTheme ? { color: checkoutTheme.primaryText } : undefined}
          >
            Tip
          </div>
          <div
            className={cn(
              "shrink-0 text-right text-sm tabular-nums",
              !checkoutTheme && "text-gray-500 dark:text-gray-400"
            )}
            style={checkoutTheme ? { color: checkoutTheme.mutedText } : undefined}
          >
            ${formatCentsAsUsd(tipAmount)}
          </div>
        </div>
      ) : null}
      {!hideFooterTotal && total !== undefined ? (
        <CheckoutOrderFooterTotal
          amountCents={
            total +
            (showShippingLineItem ? shippingCents : 0) +
            (showShippingProtectionLineItem ? shippingProtectionCents : 0) +
            (showOrderBumpLineItem ? orderBumpCents : 0) +
            tipAmount
          }
          checkoutColors={checkoutColors}
        />
      ) : null}
    </div>
  );
}
